import APIClientAbstract from "core/src/api-client-abstract";
import Log from "utils/src/log";
import deferredPromise from "core/src/utils/deferred-promise";
import {def, validate} from 'utils/src/validation';
import {joinPath} from 'core/src/utils/legacy';
import {get, isPlainObject} from 'lodash';
import Socket from "core/src/utils/socket";
import Error from 'utils/src/error';

const log = Log.instance("client/core/api");

const socket = new Socket();

export default class APIClient extends APIClientAbstract{
	/**
	 * Open a socket.io connection to one of the server's namespaces.
	 * socket.io resolves a bare namespace ('/user') against the page's own origin, which is the
	 * hosting page when this client is embedded in another site. Prefix the configured server, so
	 * the connection is always made to Graphileon rather than to the host.
	 * @param {string} [namespace]	The namespace to connect to, e.g. '/user'.
	 * @param {object} [options]	Options passed on to socket.io.
	 * @returns {Socket}
	 */
	io(namespace = '/', options = {}) {
		const server = this.config.server;
		const url = server ? joinPath(server, namespace) : namespace;
		return socket.io(url, {
			// Send credentials, so the connection is authenticated the same way requests are.
			withCredentials: true,
			...options
		});
	}

	setToken(token) {
		super.setToken(token);
	}

	/**
	 * Sends a request (with authentication headers) to the API.
	 * @param {string} uri The endpoint to send the request to.
	 * @param {string} [method] The HTTP method to use. Defaults to GET.
	 * @param {object} [data] The payload data for the endpoint.
	 * @returns {Promise}
	 */
	_executeRequest(uri, method = 'GET', data = {}) {
		let promise = new Promise((resolve, reject) => {
			let check = validate({
				uri: [uri, 'isString'],
				method: [method, 'isString'],
				data: [data, 'isObject', {default: undefined, warn: def(data)}]
			}, "Could not execute " + method + " request to " + uri + '.');
			if (!check.isValid()) {
				return reject(check.createError());
			}
			const valid = check.getValue();

			data = valid.data;
			let url = joinPath(this.config.server, 'api', valid.uri);
			const timeout = get(data, 'timeout', this.config.requestTimeoutMs);
			if (!valid.method.match(/get/i)) {
				$.ajaxSetup({
					contentType: 'application/json',
				});
				data = JSON.stringify(data);
			}

			let request = $.ajax({
				type: valid.method,
				url,
				crossDomain: true,
				timeout: timeout,
				beforeSend: (xhr) => {
					// Send the token explicitly, so authentication also works when the client is
					// hosted on a different origin than the server (third-party cookies blocked).
					if (this.token) {
						xhr.setRequestHeader('X-Token', this.token);
					}
					if (this.config.frontendUrl) {
						xhr.setRequestHeader('x-graphileon-frontend-url', this.config.frontendUrl);
					}
				},
				xhrFields: {
					withCredentials: true
				},
				data,
				dataType: 'json'
			});

			request.fail(error => {
				reject(this.getResponseError(error));
			});

			request.done((response, status, xhr) => {
				this.readRefreshedToken(xhr);
				resolve(response);
			});
		});
		return promise;
	}

	/**
	 * The server refreshes the token's expiry on every request and returns it in the `X-Token`
	 * header. Adopt it, so the session does not expire while the client is still active.
	 *
	 * Only adopts a refreshed token while the client still holds one: if the session was cleared
	 * in the meantime (logout, or an `InvalidToken` response to a concurrent request), a response
	 * that was already in flight must not resurrect the token.
	 * @param {object} xhr
	 */
	readRefreshedToken(xhr) {
		if (!xhr || typeof xhr.getResponseHeader !== 'function') {
			return;
		}
		if (!this.token) {
			return; // no active session to refresh
		}
		let token;
		try {
			token = xhr.getResponseHeader('X-Token');
		} catch (e) {
			return; // header not exposed by CORS
		}
		if (token && token !== this.token) {
			this.fire(APIClient.Event.TOKEN_REFRESHED, token);
		}
	}

	getResponseError(response) {
		if (!isPlainObject(response.responseJSON)) {
			let message = `Invalid server response`;
			if(response.statusText) {
				if(response.statusText === 'timeout') {
					message = 'Server timed out.';
				} else {
					message += ` (${response.statusText})`;
				}
			}
			// Server should always provide a json response on errors.
			return new Error(message, response);
		}

		const error = get(response, 'responseJSON.error', "An error occurred.")
		return new Error(error);
	}

	/**
	 * Upload a file from FormData
	 * @param formData
	 * @return {*}
	 */
	upload(file) {
		let formData = new FormData();
		formData.append("file", file);
		let promise = new Promise((resolve, reject) => {
			let url = joinPath(this.config.server, '/api/file/create');
			this.loadingRequestStarted();
			let request = $.ajax({
				type: 'POST',
				url: url,
				processData: false,
				contentType: false,
				crossDomain: true,
				beforeSend: (xhr) => {
					if (get(this, 'config.environment') !== undefined) {
						xhr.setRequestHeader('X-environment', this.config.environment);
					}
					if (this.token) {
						xhr.setRequestHeader('X-Token', this.token);
					}
					if (this.config.frontendUrl) {
						xhr.setRequestHeader('x-graphileon-frontend-url', this.config.frontendUrl);
					}
				},
				data: formData,
				dataType: 'json',
				xhrFields: {
					withCredentials: true
				},
			});
			request.always(() => {
				this.loadingRequestFinished();
			});
			request.fail(error => {
				log.error('API Upload Error' + url);
				reject(this.handleError(this.getResponseError(error)));
			});
			request.done(response => {
				resolve(response.data);
			});
		});
		return deferredPromise(promise);
	};

}
